<template>
  <header class="seo-nav" :class="{ 'is-scrolled': scrolled }">
    <nav class="container d-flex align-items-center justify-content-between py-3">
      <router-link :to="home" class="d-flex align-items-center gap-2 text-decoration-none">
        <img width="46" height="46" src="/logo.png" alt="Pine Fitness logo">
        <h4 class="text-dark logo-name mb-0">PineFitness</h4>
      </router-link>

      <!-- Desktop menu -->
      <ul class="d-none d-md-flex nav gap-3 align-items-center mb-0">
        <li class="nav-item seo-feature" :class="{ 'is-open': featOpen }" ref="feat">
          <button type="button" class="nav-link seo-feature__btn" :aria-expanded="String(featOpen)" @click.stop="featOpen = !featOpen; studiosOpen = false">
            {{ t.features }}
            <svg class="chev" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <div class="seo-mega seo-mega--single" v-show="featOpen">
            <div class="seo-mega__grid">
              <router-link v-for="l in featureLinks" :key="l.slug" :to="linkTo(l.slug)" @click="featOpen = false">{{ t[l.k] }}</router-link>
            </div>
          </div>
        </li>
        <li class="nav-item seo-feature" :class="{ 'is-open': studiosOpen }" ref="studios">
          <button type="button" class="nav-link seo-feature__btn" :aria-expanded="String(studiosOpen)" @click.stop="studiosOpen = !studiosOpen; featOpen = false">
            {{ t.studios }}
            <svg class="chev" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <div class="seo-mega seo-mega--single" v-show="studiosOpen">
            <div class="seo-mega__grid">
              <router-link v-for="l in studioLinks" :key="l.slug" :to="linkTo(l.slug)" @click="studiosOpen = false">{{ t[l.k] }}</router-link>
            </div>
          </div>
        </li>
        <li v-for="l in directLinks" :key="l.hash" class="nav-item d-none d-xl-flex">
          <a class="nav-link seo-direct-link" :href="homeAnchor(l.hash)">{{ t[l.k] }}</a>
        </li>
      </ul>

      <div class="d-flex align-items-center gap-2">
        <div class="lang-switcher position-relative d-none d-sm-inline" ref="langSw">
          <button type="button" class="btn d-flex align-items-center gap-2 lang-btn" :aria-expanded="String(langOpen)" @click.stop="langOpen = !langOpen">
            <img class="lang-flag" :src="current.flag" :alt="current.flagAlt" width="20" height="14">
            <span class="lang-code">{{ current.label }}</span>
            <svg class="chev" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <ul v-show="langOpen" class="lang-menu" role="listbox">
            <li v-for="l in flags" :key="l.code">
              <button type="button" class="lang-item" :aria-selected="l.code === lang" @click="switchLang(l.code)">
                <img class="lang-flag" :src="l.flag" :alt="l.flagAlt" width="20" height="14">
                <span class="lang-name">{{ l.name }} ({{ l.label }})</span>
              </button>
            </li>
          </ul>
        </div>
        <a class="btn btn-outline-glass d-none d-sm-inline-flex" :href="homeAnchor('contactForm')">{{ t.demo }}</a>
        <button type="button" class="seo-burger d-md-none" :aria-expanded="String(mobileOpen)" aria-label="Menu" @click.stop="mobileOpen = !mobileOpen">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path :d="mobileOpen ? 'M6 6l12 12M18 6L6 18' : 'M4 7h16M4 12h16M4 17h16'" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
        </button>
      </div>
    </nav>

    <!-- Mobile menu -->
    <div class="seo-mobile d-md-none" v-show="mobileOpen" ref="mobile">
      <p class="seo-mega__label">{{ t.features }}</p>
      <router-link v-for="l in featureLinks" :key="'m-' + l.slug" :to="linkTo(l.slug)" @click="mobileOpen = false">{{ t[l.k] }}</router-link>
      <p class="seo-mega__label seo-mobile__label2">{{ t.studios }}</p>
      <router-link v-for="l in studioLinks" :key="'m-' + l.slug" :to="linkTo(l.slug)" @click="mobileOpen = false">{{ t[l.k] }}</router-link>
      <p class="seo-mega__label seo-mobile__label2">{{ t.learn }}</p>
      <a v-for="l in directLinks" :key="'m-direct-' + l.hash" :href="homeAnchor(l.hash)" @click="mobileOpen = false">{{ t[l.k] }}</a>
      <div class="seo-mobile__foot">
        <div class="seo-mobile__lang">
          <button v-for="l in flags" :key="'ml-' + l.code" type="button" class="seo-mobile__langbtn" :class="{ 'is-active': l.code === lang }" @click="switchLang(l.code); mobileOpen = false">
            <img class="lang-flag" :src="l.flag" :alt="l.flagAlt" width="20" height="14"><span>{{ l.label }}</span>
          </button>
        </div>
        <a class="btn btn-outline-glass" :href="homeAnchor('contactForm')" @click="mobileOpen = false">{{ t.demo }}</a>
      </div>
    </div>
  </header>
</template>

<script>
import seoSlugs from '@/seo/seo-slugs.json'

const LABELS = {
  en: { features: 'Features', studios: 'Studios', learn: 'Learn more', how: 'How it works', pricing: 'Pricing', faq: 'FAQ', bookings: 'Class bookings', billing: 'Memberships & billing', coaching: 'Coaching & programs', marketing: 'Marketing', online: 'Online classes', corporate: 'Corporate', gym: 'Gym', fitness: 'Fitness', pilates: 'Pilates', yoga: 'Yoga', demo: 'Request demo' },
  el: { features: 'Χαρακτηριστικά', studios: 'Στούντιο', learn: 'Μάθετε περισσότερα', how: 'Πώς λειτουργεί', pricing: 'Τιμολόγηση', faq: 'Συχνές ερωτήσεις', bookings: 'Κρατήσεις', billing: 'Συνδρομές & χρεώσεις', coaching: 'Προπόνηση & προγράμματα', marketing: 'Marketing', online: 'Online μαθήματα', corporate: 'Εταιρικά', gym: 'Γυμναστήριο', fitness: 'Fitness', pilates: 'Pilates', yoga: 'Yoga', demo: 'Αίτημα επίδειξης' }
}

const FLAGS = {
  en: { code: 'en', label: 'EN', name: 'English', flag: require('@/assets/images/flag/rect/gb.png'), flagAlt: 'United Kingdom flag' },
  el: { code: 'el', label: 'EL', name: 'Ελληνικά', flag: require('@/assets/images/flag/rect/gr.png'), flagAlt: 'Greece flag' }
}

const FEATURE_LINKS = [
  { slug: 'class-booking-software', k: 'bookings' },
  { slug: 'gym-membership-billing-software', k: 'billing' },
  { slug: 'personal-training-software', k: 'coaching' },
  { slug: 'fitness-marketing-software', k: 'marketing' },
  { slug: 'online-class-booking-software', k: 'online' },
  { slug: 'corporate-wellness-software', k: 'corporate' }
]
const STUDIO_LINKS = [
  { slug: 'gym-management-software', k: 'gym' },
  { slug: 'fitness-studio-software', k: 'fitness' },
  { slug: 'pilates-studio-software', k: 'pilates' },
  { slug: 'yoga-studio-software', k: 'yoga' }
]
const DIRECT_LINKS = [
  { hash: 'how', k: 'how' },
  { hash: 'pricing', k: 'pricing' },
  { hash: 'faq', k: 'faq' }
]

export default {
  name: 'SiteNav',
  data() {
    return {
      featOpen: false,
      studiosOpen: false,
      langOpen: false,
      mobileOpen: false,
      scrolled: false,
      featureLinks: FEATURE_LINKS,
      studioLinks: STUDIO_LINKS,
      directLinks: DIRECT_LINKS,
      flags: [FLAGS.en, FLAGS.el]
    }
  },
  computed: {
    rawSlug() {
      return this.$route.path.replace(/^\/el/, '').replace(/^\//, '').replace(/\/$/, '')
    },
    isSeoRoute() {
      return seoSlugs.indexOf(this.rawSlug) !== -1
    },
    lang() {
      const p = this.$route.path
      return (p === '/el' || p.indexOf('/el/') === 0) ? 'el' : 'en'
    },
    home() {
      return this.lang === 'el' ? '/el' : '/'
    },
    t() {
      return LABELS[this.lang]
    },
    current() {
      return FLAGS[this.lang]
    }
  },
  methods: {
    linkTo(slug) {
      return (this.lang === 'el' && seoSlugs.indexOf(slug) !== -1) ? '/el/' + slug : '/' + slug
    },
    homeAnchor(hash) {
      return `${this.home}#${hash}`
    },
    switchLang(code) {
      this.langOpen = false
      if (code === this.lang) return
      // URL drives language everywhere: homepage (/ vs /el) and SEO pages (/slug vs /el/slug).
      const base = this.rawSlug ? '/' + this.rawSlug : ''
      this.$router.push(code === 'el' ? '/el' + base : (base || '/'))
    }
  },
  mounted() {
    this._onDoc = (e) => {
      if (this.$refs.feat && !this.$refs.feat.contains(e.target)) this.featOpen = false
      if (this.$refs.studios && !this.$refs.studios.contains(e.target)) this.studiosOpen = false
      if (this.$refs.langSw && !this.$refs.langSw.contains(e.target)) this.langOpen = false
    }
    this._onScroll = () => { this.scrolled = window.scrollY > 10 }
    document.addEventListener('click', this._onDoc)
    window.addEventListener('scroll', this._onScroll, { passive: true })
  },
  beforeUnmount() {
    if (this._onDoc) document.removeEventListener('click', this._onDoc)
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll)
  }
}
</script>
